import BulkUploadModal from '@components/Projects/BulkUpload/BulkUploadModal';
import FolderModal from '@components/Projects/FolderModal';
import MoveDialog from '@components/Projects/MoveDialog';
import ShareDialog from '@components/Projects/ShareDialog';
import Thumbnail from '@components/Projects/Thumbnail';
import ProjectUploadModal from '@components/Projects/UploadModal';
import DeleteConfirmationDialog from '@components/Shared/Dialog/DeleteConfirmationDialog';
import Header from '@components/Shared/Layout/Header';
import SearchInput from '@components/Shared/SearchInput';
import { ROUTE_PATHS } from '@constants/routes';
import { useLocalization } from '@lib/localization';
import { has } from '@lib/permission';
import { buildFolderPath, buildProjectPath } from '@lib/routing/projectPath';
import { useProjectRoute } from '@lib/routing/useProjectRoute';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box, Button, Chip, IconButton, LinearProgress, ListItemIcon, Menu, MenuItem, Paper, Stack, type Theme, Tooltip, Typography
} from '@mui/material';
import {
  DataGrid, type GridColDef, type GridEventListener, type GridRowParams, type GridRowsProp, type GridSlots
} from '@mui/x-data-grid';
import {
  type IFolder, type IFolderChild, isFolder, isProject, useDeleteFolderMutation, useGetFolderByPathQuery
} from '@store/api/folderEndpoints';
import {
  folderPathOf,
  type IProject,
  useAddProjectMutation, useDeleteProjectMutation, useGetProjectsQuery, useUpdateProjectMutation
} from '@store/api/projectEndpoints';
import { useDebounce } from '@store/hooks/useDebounce';
import React, { useEffect, useState } from 'react';
import { EditIcon, PlusIcon, RefreshIcon, ShareAltIcon, TrashIcon, UploadIcon } from 'react-line-awesome';
import { createSearchParams, useMatch, useNavigate, useSearchParams } from 'react-router-dom';

export type CustomFolderChildRow = IFolderChild & { id: string, name: string, dateCreated: string };

// Team root folders are managed through the Teams page; the folder API rejects
// edit/delete on them for every role, admins included.
const isTeamRootRow = (row: CustomFolderChildRow): boolean =>
  row.type === 'Folder' && Boolean((row.item as IFolder).teamId);

// Cross-team shared folders surface at root level for target-team members.
const isSharedInRow = (row: CustomFolderChildRow): boolean =>
  row.type === 'Folder' && Boolean((row.item as IFolder).isSharedWithMe);

const ProjectsPage = () => {
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [bulkUploadModalOpen, setBulkUploadModalOpen] = useState(false);
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  // Kept apart from `selectedItem`, which is cleared on close: the dialog still shows the
  // item while it animates out, and the add-folder modal reads `selectedItem` as "rename".
  const [moveItem, setMoveItem] = useState<CustomFolderChildRow | null>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedItem, setSelectedItem] = useState<CustomFolderChildRow | null>();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const navigate = useNavigate();
  const [addProject, { isLoading: isAddProjectLoading }] = useAddProjectMutation();
  const [_, { isLoading: isUpdateProjectLoading }] = useUpdateProjectMutation();
  const [deleteProject] = useDeleteProjectMutation();
  const [deleteFolder] = useDeleteFolderMutation();
  const { t } = useLocalization();

  const isProjectsPage = useMatch(ROUTE_PATHS.PROJECTS);
  const { folderPath } = useProjectRoute();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search') ?? '';
  const debouncedSearchTerm = useDebounce(search, 500);
  const { data: folder, isLoading: isFolderLoading, refetch: folderRefetch, isFetching: isFolderFetching }
    = useGetFolderByPathQuery(folderPath || '/', { skip: Boolean(debouncedSearchTerm) });
  const { data: projects, isLoading: isProjectsLoading, refetch: projectsRefetch, isFetching: isProjectsFetching }
    = useGetProjectsQuery({ search: debouncedSearchTerm }, { skip: !debouncedSearchTerm });

  // Every row and the folder itself arrive with their own permission list, so both the
  // toolbar and the row actions read the answer straight off the resource.
  const canCreateFolder = has(folder, 'folder:post');
  const canUploadProject = has(folder, 'project:post') && has(folder, 'inspection:post');

  const selectedResource = selectedItem?.item as IFolder | IProject | undefined;

  const canEditSelected = Boolean(selectedItem) && (selectedItem?.type === 'Folder'
    ? has(selectedResource, 'folder:put')
    : has(selectedResource, 'project:patch'));
  const canDeleteSelected = Boolean(selectedItem) && (selectedItem?.type === 'Folder'
    ? has(selectedResource, 'folder:delete')
    : has(selectedResource, 'project:delete'));
  const canShareSelected = selectedItem?.type === 'Folder' && has(selectedResource, 'folder:share');

  // The share dialog reads live share data, so resolve the selected folder from the query cache.
  const shareTargetFolder = selectedItem?.type === 'Folder'
    ? folder?.children.filter(isFolder).find((child) => child.item._id === selectedItem.id)?.item
    ?? (selectedItem.item as IFolder)
    : null;

  const disabledActionTooltip = t('Permission_NotAllowed');

  useEffect(() => {
    if (search) {
      navigate({
        pathname: ROUTE_PATHS.PROJECTS,
        search: createSearchParams({ search: search }).toString()
      });

    } else {
      if (isProjectsPage) navigate({ pathname: ROUTE_PATHS.FOLDERS });
    }

  }, [isProjectsPage, navigate, searchParams, search]);


  const actionMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, row: CustomFolderChildRow) => {
    event.stopPropagation();
    event.preventDefault();

    setAnchorEl(event.currentTarget);
    setSelectedItem(row);
  };

  const actionMenuClose = (_event: React.SyntheticEvent | Event, reason?: string) => {
    unfocusProject(!!reason);
  };

  const unfocusProject = (cleanSelectedProject: boolean = false) => {
    setAnchorEl(null);
    if (cleanSelectedProject) setSelectedItem(null);
  };

  const handleRename = () => {
    setFolderModalOpen(true);
    unfocusProject();
  };

  const handleMove = () => {
    setMoveItem(selectedItem ?? null);
    setMoveDialogOpen(true);
    unfocusProject(true);
  };

  const handleShare = () => {
    setShareDialogOpen(true);
    unfocusProject();
  };

  const handleShareCanceled = () => {
    setShareDialogOpen(false);
    setSelectedItem(null);
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
    unfocusProject();
  };

  const handleItemClick: GridEventListener<'rowClick'> = (params: GridRowParams<CustomFolderChildRow>) => {
    const row = params.row;
    const type = row.type;

    if (type === 'Project') {
      // A project's URL carries the folder it lives in. In a listing that is the folder in
      // view; in search results the rows come from all over the tree and carry their own.
      const rowFolderPath = folderPathOf(row.item as IProject) || folder?.path || '';

      navigate(buildProjectPath({ folderPath: rowFolderPath, projectId: params.id.toString() }));
    } else if (type === 'Folder') {
      navigate(buildFolderPath((row.item as IFolder).path));
    }
  };

  const handleDeleteConfirmation = async () => {
    if (selectedItem) {
      if (selectedItem.type === 'Project') await deleteProject(selectedItem.id);
      if (selectedItem.type === 'Folder') await deleteFolder(selectedItem.item as IFolder);
    }
    setSelectedItem(null);
  };

  const handleDeleteCanceled = async () => {
    setDeleteDialogOpen(false);
    setSelectedItem(null);
  };

  const handleFolderCanceled = async () => {
    setFolderModalOpen(false);
    setSelectedItem(null);
  };

  const columns: GridColDef<CustomFolderChildRow>[] = [
    {
      field: 'type',
      headerName: '',
      width: 120,
      // sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => <Thumbnail type={params.row.type} projectId={params.row.id} />,
    },
    {
      field: 'name',
      headerName: t('Common_Name'),
      flex: 7,
      renderCell: (params) => (
        <Box sx={nameCellStyle}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body1" align="center">
              {params.value}
            </Typography>
            {isTeamRootRow(params.row) && <Chip size="small" label={t('Folder_Team_Chip')} sx={teamChipStyles} />}
            {isSharedInRow(params.row) && <Chip size="small" label={t('Folder_Shared_Chip')} sx={teamChipStyles} />}
          </Stack>
          {params.row.type != 'Folder' &&
            <Typography variant="body2" color="hsl(220deg 20% 65% / 60%)" align="center">
              {/* Search results come from all over the tree, so where a project lives is the
                  question being asked; inside a folder listing the answer is already the page. */}
              {(isProjectsPage && folderPathOf(params.row.item as IProject)) || params.row.id}
            </Typography>}
        </Box>
      ),
    },
    {
      field: 'dateCreated',
      headerName: t('Common_DateCreated'),
      width: 150,
      renderCell: (params) => params.value ? new Date(params.value).toAgoString() : ''
    },
    {
      field: 'actions',
      headerName: '',
      width: 70,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      editable: false,
      headerAlign: 'right',
      align: 'right',
      renderCell: (params) => isTeamRootRow(params.row) ? null : (
        <IconButton
          aria-label="more"
          sx={{ '&:focus': { outline: 'none' } }}
          onClick={(e) => actionMenuOpen(e, params.row)}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  const folderRows = !isProjectsPage
    ? folder?.children
      .filter(child => isFolder(child))
      .map(child => ({
        id: child.item._id,
        name: child.item.name,
        dateCreated: child.item.createdAt,
        ...child
      }))
      .sort((a, b) => a.name.localeCompare(b.name)) ?? []
    : [];

  const projectRows = !isProjectsPage
    ? folder?.children
      .filter(child => isProject(child))
      .map(child => ({
        id: child.item.projectInfo.id,
        name: child.item.projectInfo.name,
        dateCreated: child.item.projectInfo.dateCreated,
        ...child
      }))
      .sort((a, b) => a.name.localeCompare(b.name)) ?? []
    : (search == debouncedSearchTerm
      ? projects?.map(project => ({
        type: 'Project' as const,
        item: project,
        id: project.projectInfo.id,
        name: project.projectInfo.name,
        dateCreated: project.projectInfo.dateCreated
      })) ?? []
      : []);

  const rows: GridRowsProp<CustomFolderChildRow> = [...folderRows, ...projectRows];

  const open = Boolean(anchorEl);

  return (
    <Box sx={containerStyles}>
      <Header />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          marginBottom: '12px'
        }}
      >
        <SearchInput
          text={search}
          placeholder={t('Folder_Search')}
          action={(value) => { setSearchParams({ search: value }); }}
          count={rows?.length ?? 0}
        />
        <Box sx={{ display: 'flex', gap: '1rem' }}>
          <Box>
            <IconButton
              onClick={!isProjectsPage ? folderRefetch : projectsRefetch}
              disabled={isFolderLoading || isFolderFetching || isProjectsLoading || isProjectsFetching
                || (Boolean(isProjectsPage) && search != debouncedSearchTerm)}>
              <RefreshIcon />
            </IconButton>
          </Box>
          {!isProjectsPage && <>
            {canCreateFolder &&
              <Button onClick={() => setFolderModalOpen(true)} variant="contained" startIcon={<PlusIcon />}>
                {t('Common_Folder')}
              </Button>}
            {canUploadProject &&
              <Button onClick={() => setUploadModalOpen(true)} variant="contained" startIcon={<UploadIcon />}>
                {t('Common_Upload')}
              </Button>}
          </>}
        </Box>
      </Box>
      <Paper sx={{ flexGrow: 1, width: '100%' }}>
        <DataGrid
          loading={isFolderLoading || isFolderFetching || isProjectsLoading
            || isProjectsFetching || isAddProjectLoading || isUpdateProjectLoading
            || (Boolean(isProjectsPage) && search != debouncedSearchTerm)}
          slots={{
            loadingOverlay: LinearProgress as GridSlots['loadingOverlay'],
            noRowsOverlay: () => <Box sx={noRowsOverlayStyles}>No project or folder found</Box>,
          }}
          rows={rows || []}
          rowHeight={75}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10, page: 0 },
            },
          }}
          disableColumnSelector
          disableRowSelectionOnClick
          onRowClick={handleItemClick}
          pageSizeOptions={[10, 20, 30]}
          sx={dataGridStyles}
          localeText={{
            MuiTablePagination: {
              labelRowsPerPage: t('DataGrid_RowsPerPage'),
              labelDisplayedRows: ({ from, to, count }) => `${from}-${to} / ${count}`
            }
          }}
        />
      </Paper>
      <ProjectUploadModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        uploadProject={async (formData: FormData) => { await addProject(formData); }}
        folder={folder}
        onBulkUploadRequested={() => {
          setUploadModalOpen(false);
          setBulkUploadModalOpen(true);
        }} />
      <BulkUploadModal
        open={bulkUploadModalOpen}
        onClose={() => setBulkUploadModalOpen(false)}
        parentFolder={folder} />
      <FolderModal
        isOpen={folderModalOpen}
        onClose={handleFolderCanceled}
        folderItem={selectedItem}
        currentFolder={folder} />
      <MoveDialog
        open={moveDialogOpen}
        onClose={() => setMoveDialogOpen(false)}
        item={moveItem} />
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCanceled}
        onConfirm={handleDeleteConfirmation}
        warningSubtitle={selectedItem?.type === 'Folder' && (selectedItem.item as IFolder).children.length
          ? t('Folder_Delete_WarningSubtitle', { count: (selectedItem.item as IFolder).children.length })
          : undefined} />
      <Menu id="row-menu" anchorEl={anchorEl} open={open} onClose={actionMenuClose}>
        {/* A project's name comes from its file, so only folders can be renamed here. */}
        {selectedItem?.type === 'Folder' &&
          <Tooltip title={!canEditSelected ? disabledActionTooltip : ''} placement="left">
            <div>
              <MenuItem
                onClick={handleRename}
                disabled={!canEditSelected}
                sx={{ padding: '12px 20px' }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <EditIcon />
                </ListItemIcon>
                {t('Common_Rename')}
              </MenuItem>
            </div>
          </Tooltip>}
        <Tooltip title={!canEditSelected ? disabledActionTooltip : ''} placement="left">
          <div>
            <MenuItem
              onClick={handleMove}
              disabled={!canEditSelected}
              sx={{ padding: '12px 20px' }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <DriveFileMoveOutlinedIcon fontSize="small" />
              </ListItemIcon>
              {t('Folder_Move_Action')}
            </MenuItem>
          </div>
        </Tooltip>
        {selectedItem?.type === 'Folder' &&
          <Tooltip title={!canShareSelected ? disabledActionTooltip : ''} placement="left">
            <div>
              <MenuItem
                onClick={handleShare}
                disabled={!canShareSelected}
                sx={{ padding: '12px 20px' }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <ShareAltIcon />
                </ListItemIcon>
                {t('Folder_Share')}
              </MenuItem>
            </div>
          </Tooltip>}
        <Tooltip title={!canDeleteSelected ? disabledActionTooltip : ''} placement="left">
          <div>
            <MenuItem
              onClick={handleDelete}
              disabled={!canDeleteSelected}
              sx={{ padding: '12px 20px' }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <TrashIcon />
              </ListItemIcon>
              {t('Common_Delete')}
            </MenuItem>
          </div>
        </Tooltip>
      </Menu>
      <ShareDialog
        open={canShareSelected && shareDialogOpen}
        onClose={handleShareCanceled}
        folder={shareTargetFolder} />
    </Box >
  );
};

const containerStyles = {
  display: 'flex',
  flexDirection: 'column' as const,
  width: '100%',
};

const dataGridStyles = (theme: Theme) => ({
  height: '100%',
  width: '100%',
  backgroundColor: theme.palette.background.paper,

  '& .MuiDataGrid-columnHeader': {
    backgroundColor: theme.palette.background.paper,
  },
  '& .MuiDataGrid-cell:focus': { outline: 'none' },
  '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
  '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
    outline: 'none',
  },
  '& .MuiDataGrid-row:hover': {
    cursor: 'pointer',
  },
});

const nameCellStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  justifyContent: 'center',
  alignItems: 'start',
  height: '100%',
  width: '100%',
};

const teamChipStyles = {
  height: 18,
  fontSize: 10.5,
  fontWeight: 500,
  color: 'text.secondary',
  backgroundColor: 'transparent',
  border: '1px solid',
  borderColor: 'divider',
  '& .MuiChip-label': { px: 0.75 },
};

const noRowsOverlayStyles = {
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#666',
  fontStyle: 'italic',
};

export default ProjectsPage;
